import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Search,
  Phone,
  Mail,
  ArrowRight,
  ExternalLink,
  UserCheck,
  Calendar,
  MessageSquare,
  Users,
  GraduationCap,
  Sparkles,
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { Button } from "src/components/ui/Button";
import { Badge } from "src/components/ui/Badge";
import { Input } from "src/components/ui/Input";
import { demoRequestsService, DemoRequest } from "src/services/demoRequestsService";
import { useInquiries } from "src/hooks/useInquiriesQuery";
import { useUsers, User } from "src/hooks/useUsers";
import { ROUTES } from "src/routes/RouteConfig";

type WorkstationTab = "leads" | "inquiries" | "users";

const formatRelativeTime = (dateString?: string | null): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const AdminTriageWorkstation: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<WorkstationTab>("leads");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // 1. Fetch Demo Requests / Leads
  const {
    data: demoRequests = [],
    isLoading: isLeadsLoading,
    refetch: refetchLeads,
  } = useQuery({
    queryKey: ["workstation-demo-requests"],
    queryFn: () => demoRequestsService.fetchNewList(25),
    staleTime: 3 * 60 * 1000,
  });

  // 2. Fetch Inquiries
  const {
    data: inquiries = [],
    isLoading: isInquiriesLoading,
    refetch: refetchInquiries,
  } = useInquiries(25);

  // 3. Fetch Users
  const {
    data: usersData,
    isLoading: isUsersLoading,
    refetch: refetchUsers,
  } = useUsers();

  const allUsers = usersData?.users || [];

  // Filtered Leads
  const filteredLeads = useMemo(() => {
    return demoRequests.filter((req) => {
      const matchesSearch =
        searchQuery === "" ||
        req.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.parent_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.parent_phone?.includes(searchQuery);

      const matchesStatus =
        statusFilter === "all" || req.status?.toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [demoRequests, searchQuery, statusFilter]);

  // Filtered Inquiries
  const filteredInquiries = useMemo(() => {
    return inquiries.filter((inq) => {
      const matchesSearch =
        searchQuery === "" ||
        inq.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inq.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inq.message?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inq.inquiry_type?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || inq.status?.toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [inquiries, searchQuery, statusFilter]);

  // Filtered Users
  const filteredUsers = useMemo(() => {
    return allUsers.filter((u) => {
      const fullName = `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase();
      const matchesSearch =
        searchQuery === "" ||
        fullName.includes(searchQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        u.role?.toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [allUsers, searchQuery, statusFilter]);

  const handleRefresh = () => {
    if (activeTab === "leads") refetchLeads();
    else if (activeTab === "inquiries") refetchInquiries();
    else if (activeTab === "users") refetchUsers();
  };

  const pendingLeadsCount = demoRequests.filter(
    (r) => !r.status || r.status === "new" || r.status === "unassigned"
  ).length;
  const pendingInquiriesCount = inquiries.filter((i) => i.status === "new").length;

  return (
    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
      {/* 1. Workstation Top Utility Bar */}
      <div className="bg-slate-900 text-white px-6 py-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h2 className="text-lg md:text-xl font-bold tracking-tight text-white">
              Admin Triage Workstation
            </h2>
            <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-300 border border-indigo-400/30">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse"></span>
              Unified Queue
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Single-pane workflow for incoming leads, parent inquiries, and registered users • IST (UTC+05:30)
          </p>
        </div>

        {/* Tab Controls */}
        <div className="flex items-center bg-slate-800/90 p-1 rounded-xl border border-slate-700/80 text-xs font-semibold shrink-0">
          <button
            type="button"
            onClick={() => {
              setActiveTab("leads");
              setStatusFilter("all");
            }}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg transition-all duration-150 ${
              activeTab === "leads"
                ? "bg-indigo-600 text-white shadow-sm font-bold"
                : "text-slate-400 hover:text-white hover:bg-slate-700/50"
            }`}
          >
            <span>Inbound Leads</span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-mono ${
                activeTab === "leads"
                  ? "bg-white/20 text-white font-bold"
                  : "bg-slate-700 text-slate-300"
              }`}
            >
              {pendingLeadsCount > 0 ? pendingLeadsCount : demoRequests.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveTab("inquiries");
              setStatusFilter("all");
            }}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg transition-all duration-150 ${
              activeTab === "inquiries"
                ? "bg-indigo-600 text-white shadow-sm font-bold"
                : "text-slate-400 hover:text-white hover:bg-slate-700/50"
            }`}
          >
            <span>Support Inquiries</span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-mono ${
                activeTab === "inquiries"
                  ? "bg-white/20 text-white font-bold"
                  : "bg-slate-700 text-slate-300"
              }`}
            >
              {pendingInquiriesCount > 0 ? pendingInquiriesCount : inquiries.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveTab("users");
              setStatusFilter("all");
            }}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg transition-all duration-150 ${
              activeTab === "users"
                ? "bg-indigo-600 text-white shadow-sm font-bold"
                : "text-slate-400 hover:text-white hover:bg-slate-700/50"
            }`}
          >
            <span>Recent Users</span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-mono ${
                activeTab === "users"
                  ? "bg-white/20 text-white font-bold"
                  : "bg-slate-700 text-slate-300"
              }`}
            >
              {allUsers.length}
            </span>
          </button>
        </div>
      </div>

      {/* 2. Filter & Search Toolbar */}
      <div className="bg-slate-50/80 border-b border-slate-200 px-6 py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                activeTab === "leads"
                  ? "Filter by student, parent, email..."
                  : activeTab === "inquiries"
                  ? "Filter by sender, message, type..."
                  : "Filter by user name, email..."
              }
              className="pl-9 text-xs h-9 bg-white border-slate-300 rounded-lg focus-visible:ring-indigo-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs h-9 bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-slate-700 font-medium outline-none focus:border-indigo-500"
          >
            {activeTab === "leads" && (
              <>
                <option value="all">All Lead Statuses</option>
                <option value="new">New / Unassigned</option>
                <option value="contacted">Contacted</option>
                <option value="completed">Demo Completed</option>
              </>
            )}
            {activeTab === "inquiries" && (
              <>
                <option value="all">All Inquiry Statuses</option>
                <option value="new">New / Unread</option>
                <option value="responded">Responded</option>
                <option value="closed">Closed</option>
              </>
            )}
            {activeTab === "users" && (
              <>
                <option value="all">All Roles & Types</option>
                <option value="student">Students</option>
                <option value="tutor">Tutors</option>
                <option value="admin">Admins</option>
              </>
            )}
          </select>
        </div>

        <div className="flex items-center gap-3 text-xs text-slate-500 shrink-0">
          <span>
            {activeTab === "leads" && `Showing ${filteredLeads.length} of ${demoRequests.length}`}
            {activeTab === "inquiries" && `Showing ${filteredInquiries.length} of ${inquiries.length}`}
            {activeTab === "users" && `Showing ${filteredUsers.length} of ${allUsers.length}`}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            className="h-8 px-2 text-slate-600 hover:text-slate-900"
            title="Refresh queue"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* 3. Table Views */}
      <div className="overflow-x-auto">
        {/* TAB 1: INBOUND LEADS / DEMO REQUESTS */}
        {activeTab === "leads" && (
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider font-semibold border-b border-slate-200">
              <tr>
                <th className="px-6 py-3">Lead / Family</th>
                <th className="px-6 py-3">Grade & Curriculum</th>
                <th className="px-6 py-3">Primary Target Goals</th>
                <th className="px-6 py-3">Contact Information</th>
                <th className="px-6 py-3">Requested</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Instant Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLeadsLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-400">
                    <div className="inline-flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin text-indigo-600" />
                      <span>Loading inbound demo requests...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                    No matching demo requests found.
                  </td>
                </tr>
              ) : (
                filteredLeads.map((req) => (
                  <tr key={req.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-6 py-3.5">
                      <div className="font-bold text-slate-900 text-sm">{req.student_name}</div>
                      <div className="text-slate-500 text-xs mt-0.5">
                        Parent: <span className="text-slate-700 font-medium">{req.parent_name || "-"}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-slate-800 font-medium border border-slate-200/60">
                        {req.grade ? `Grade ${req.grade}` : "General"}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 max-w-xs">
                      {req.primary_goals && req.primary_goals.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {req.primary_goals.slice(0, 2).map((goal, i) => (
                            <span
                              key={i}
                              className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded text-xs font-medium border border-indigo-100"
                            >
                              {goal}
                            </span>
                          ))}
                          {req.primary_goals.length > 2 && (
                            <span className="text-xs text-slate-400 self-center">
                              +{req.primary_goals.length - 2} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="font-mono text-slate-800 text-xs flex items-center gap-1.5">
                        <Phone className="w-3 h-3 text-slate-400" />
                        <span>{req.phone_isd_code ? `${req.phone_isd_code} ` : ""}{req.parent_phone || "-"}</span>
                      </div>
                      <div className="text-slate-500 text-xs flex items-center gap-1.5 mt-0.5">
                        <Mail className="w-3 h-3 text-slate-400" />
                        <span className="truncate max-w-[170px]">{req.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3.5 font-mono text-slate-600 whitespace-nowrap">
                      {formatRelativeTime(req.created_at)}
                    </td>
                    <td className="px-6 py-3.5">
                      {req.status === "new" || !req.status ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                          Unassigned
                        </span>
                      ) : req.status === "contacted" ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                          Contacted
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {req.status}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3.5 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          size="sm"
                          onClick={() => navigate(ROUTES.ADMIN_SESSIONS_SCHEDULE.path)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs h-8 px-3 shadow-sm"
                        >
                          Match Tutor
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(ROUTES.ADMIN_DEMO_BOOKINGS.path)}
                          className="text-xs h-8 px-2.5 text-slate-700"
                        >
                          Details
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* TAB 2: SUPPORT INQUIRIES */}
        {activeTab === "inquiries" && (
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider font-semibold border-b border-slate-200">
              <tr>
                <th className="px-6 py-3">Inquirer / Sender</th>
                <th className="px-6 py-3">Category / Topic</th>
                <th className="px-6 py-3">Message Preview</th>
                <th className="px-6 py-3">Submitted</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isInquiriesLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                    <div className="inline-flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin text-indigo-600" />
                      <span>Loading support inquiries...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredInquiries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                    No matching inquiries found.
                  </td>
                </tr>
              ) : (
                filteredInquiries.map((inq) => (
                  <tr key={inq.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-6 py-3.5">
                      <div className="font-bold text-slate-900 text-sm">{inq.name}</div>
                      <div className="text-slate-500 text-xs font-mono">{inq.email}</div>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                        {inq.inquiry_type ? inq.inquiry_type.replace(/_/g, " ") : "General Inquiry"}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 max-w-sm">
                      <p className="text-slate-700 line-clamp-2 leading-relaxed">
                        {inq.message || "-"}
                      </p>
                    </td>
                    <td className="px-6 py-3.5 font-mono text-slate-600 whitespace-nowrap">
                      {formatRelativeTime(inq.created_at)}
                    </td>
                    <td className="px-6 py-3.5">
                      {inq.status === "new" ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                          New Ticket
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                          {inq.status}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3.5 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(ROUTES.ADMIN_INQUIRIES.path)}
                          className="text-xs h-8 px-3 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                        >
                          Open Inbox
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* TAB 3: RECENT USERS */}
        {activeTab === "users" && (
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider font-semibold border-b border-slate-200">
              <tr>
                <th className="px-6 py-3">User Profile</th>
                <th className="px-6 py-3">Assigned Role</th>
                <th className="px-6 py-3">Account Status</th>
                <th className="px-6 py-3">Registration Date</th>
                <th className="px-6 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isUsersLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-400">
                    <div className="inline-flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin text-indigo-600" />
                      <span>Loading user directory...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    No matching users found.
                  </td>
                </tr>
              ) : (
                filteredUsers.slice(0, 15).map((user) => {
                  const displayName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email;
                  const role = user.role || "student";

                  return (
                    <tr key={user.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-700 text-xs shrink-0">
                            {displayName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-slate-900 text-sm">{displayName}</div>
                            <div className="text-slate-500 text-xs font-mono">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3.5">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${
                            role === "admin"
                              ? "bg-purple-100 text-purple-800"
                              : role === "tutor"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-slate-100 text-slate-800"
                          }`}
                        >
                          {role}
                        </span>
                      </td>
                      <td className="px-6 py-3.5">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            user.status === "active" || !user.status
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-amber-50 text-amber-700 border border-amber-200"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              user.status === "active" || !user.status ? "bg-emerald-500" : "bg-amber-500"
                            }`}
                          ></span>
                          {user.status || "active"}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 font-mono text-slate-600 whitespace-nowrap">
                        {formatRelativeTime(user.created_at)}
                      </td>
                      <td className="px-6 py-3.5 text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            role === "tutor"
                              ? navigate(`/admin/tutors/view/${user.id}`)
                              : navigate(`/admin/users/view/${user.id}`)
                          }
                          className="text-xs h-8 px-2.5 text-slate-600 hover:text-slate-900"
                        >
                          View Details →
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* 4. Bottom Footer Link */}
      <div className="bg-slate-50/60 px-6 py-3.5 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
        <span className="font-medium">
          {activeTab === "leads" && "Showing newest demo requests first"}
          {activeTab === "inquiries" && "Unread tickets marked in yellow"}
          {activeTab === "users" && "Showing newest registrations first"}
        </span>
        <button
          type="button"
          onClick={() => {
            if (activeTab === "leads") navigate(ROUTES.ADMIN_DEMO_BOOKINGS.path);
            else if (activeTab === "inquiries") navigate(ROUTES.ADMIN_INQUIRIES.path);
            else if (activeTab === "users") navigate(ROUTES.ADMIN_USERS_LIST.path);
          }}
          className="font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 transition-colors"
        >
          <span>
            {activeTab === "leads" && "Open Full Demo Bookings Roster"}
            {activeTab === "inquiries" && "Open Full Inquiries Inbox"}
            {activeTab === "users" && "Open Full User Management Directory"}
          </span>
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default AdminTriageWorkstation;
